import { IconCheck, IconChevronDown, IconLock } from "@tabler/icons-react";
import chroma from "chroma-js";
import clsx from "clsx";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { keyEnter } from "../Utils";

/** @type {ForwardRef<CardProps, HTMLDivElement>} */
const Card = forwardRef((props, ref) => {
  const {
    children,
    id,
    className,
    tabIndex,
    inert,
    onClick,
    draggable,
    onDrag,
    onDragStart,
    onDragEnd,
    onMouseOver,
    onMouseOut,
    bgColor,
    width,
    height,
    disabled,
    hideLock,
    showCheck,
    animateUnlock,
    style,
    ...otherProps
  } = props;

  const cardRef = useRef(null);
  const disabledTag = disabled ? "disabled" : "";

  // prettier-ignore
  useImperativeHandle(ref, () => {
    return cardRef.current;
  }, []);

  function init(card) {
    cardRef.current = card;
    const style = window.getComputedStyle(card);
    let color1 = bgColor || "";
    const color2 = style.getPropertyValue("--disabled-card-bg-color");

    if (color1.startsWith("var(") || color1.endsWith(")")) {
      const colorVar = /var\((.*)\)/g.exec(color1)[1];
      color1 = style.getPropertyValue(colorVar);
    }
    if (!chroma.valid(color2) || !chroma.valid(color1)) return;

    const tintedMix = chroma.mix(chroma(color1), chroma(color2), 0.6);
    card.style.setProperty(
      "--tinted-card-bg-color",
      tintedMix.darken(0.6).hex(),
    );
  }

  function showDisabledBanner() {
    // TODO
  }

  return (
    <div
      id={id}
      className={clsx("card", className, disabledTag)}
      tabIndex={tabIndex}
      draggable={draggable}
      onDrag={onDrag}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      onClick={disabled ? null : (event) => onClick?.(event)}
      onKeyDown={(event) =>
        keyEnter(event, (event) => {
          const btn = event.target.querySelector(".btn");
          if (btn) btn.click(event);
          else if (!disabled) {
            onClick?.(event);
          }
        })
      }
      style={{
        "--default-card-bg-color": bgColor,
        width,
        height,
        ...style,
      }}
      ref={(card) => card && init(card)}
      inert={inert ? "" : undefined}
      {...otherProps}
    >
      {!hideLock &&
        (disabled || animateUnlock) &&
        (showCheck ? (
          <IconCheck
            className="complete-check"
            color="var(--main-text-color)"
            stroke={3}
            size="1.8em"
          />
        ) : (
          <IconLock
            className={clsx("disabled-lock", animateUnlock && "animate-unlock")}
            color="var(--main-text-color)"
            stroke={2.5}
            size="1.7em"
          />
        ))}
      {children}
    </div>
  );
});

/**
 * @param {{
 *   className: string;
 *   loading: boolean;
 *   visible: boolean;
 *   data: any;
 *   header: React.ReactNode;
 *   children: React.ReactNode;
 *   numRows: number;
 * }} props
 */
export const ExpandableCard = (props) => {
  const {
    className,
    loading,
    visible = true,
    data,
    header: headerData,
    children: expandedInfo,
    numRows,
    ...otherProps
  } = props;

  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setExpanded(false);
  }, [data]);

  return (
    <Card
      className={clsx(
        "expandable-card",
        loading && "loading",
        expanded && "expanded",
        className,
      )}
      {...otherProps}
    >
      <div
        className="expandable-header"
        onClick={(event) => {
          if (event.target !== event.currentTarget) return;
          setExpanded(!expanded);
        }}
        style={{ opacity: visible ? 1 : 0 }}
      >
        {visible && (
          <>
            {headerData}
            <IconChevronDown
              className={clsx("expand-arrow", expanded && "open")}
              size="2rem"
              stroke={2.4}
            />
          </>
        )}
      </div>
      <div className="expanded-info" style={{ "--num-rows": numRows }}>
        {expanded && <div className="wrapper">{expandedInfo}</div>}
      </div>
    </Card>
  );
};

export default Card;
